import React from 'react';
import { useSelector } from 'react-redux';
import Flag from 'components/Flag/Flag';
import {
    fontSizes, lineHeights, mediaQueries, space
} from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import marketingFlagsUtil from 'utils/MarketingFlags';
import skuUtils from 'utils/Sku';
import { wrapFunctionalComponent } from 'utils/framework';
import { productSelector } from 'selectors/page/product/productSelector';
import SwatchesEN from 'components/ProductPage/Swatches/locales/Swatches_en_US';
import SwatchesFR from 'components/ProductPage/Swatches/locales/Swatches_fr_CA';
import { Box } from 'components/ui';

const getText = localeUtils.getLocalisedDataFromResource(SwatchesEN, SwatchesFR);

const getBadges = sku => {
    const flags = marketingFlagsUtil.getShadeFilterFlags(sku);

    return flags.length
        ? flags.map(({ text, backgroundColor }, index) => (
            <Flag
                key={index.toString()}
                data-at={Sephora.debug.dataAt('product_flag_label')}
                children={text}
                width='max-content'
                backgroundColor={backgroundColor}
                marginRight={1}
            />
        ))
        : null;
};

const renderVariationContent = (sku, variationTypeDisplayName) => {
    if (!sku?.variationValue) {
        return ' ';
    }

    return (
        <>
            {variationTypeDisplayName && `${variationTypeDisplayName}: `}
            {sku.variationValue}
            {sku.variationDesc && ` - ${sku.variationDesc}`}
        </>
    );
};

const SwatchDescription = ({ hasMinHeight }) => {
    const product = useSelector(productSelector);

    if (!product) {
        return null;
    }

    const { hoveredSku, currentSku, skuSelectorType } = product;

    const hasVariation = product.variationType !== skuUtils.skuVariationType.NONE;
    const sku = hoveredSku || currentSku;
    const skuBadges = getBadges(sku);
    const currentSkuBadges = getBadges(currentSku);

    let { variationTypeDisplayName } = product;

    if (skuUtils.isFragrance(product, sku)) {
        variationTypeDisplayName = getText('size');
    }

    const shouldPreserveSpace = currentSku?.variationValue;

    if (!skuBadges && !currentSkuBadges && !shouldPreserveSpace) {
        return null;
    }

    return !(skuSelectorType === skuUtils.skuSwatchType.NONE || !hasVariation) ? (
        <div
            data-at={Sephora.debug.dataAt('sku_name_label')}
            css={[styles.wrapper, hasMinHeight && styles.wrapperMinHeight]}
        >
            {shouldPreserveSpace && (
                <Box
                    is='span'
                    aria-hidden='true'
                    css={[styles.variationText, styles.hidden]}
                >
                    {renderVariationContent(currentSku, variationTypeDisplayName)}
                </Box>
            )}
            {currentSkuBadges && (
                <Box
                    is='span'
                    aria-hidden='true'
                    css={styles.hidden}
                    children={currentSkuBadges}
                />
            )}
            <Box css={styles.overlay}>
                {shouldPreserveSpace && (
                    <Box
                        is='span'
                        css={[styles.variationText, !sku?.variationValue && styles.hidden]}
                    >
                        {renderVariationContent(sku, variationTypeDisplayName)}
                    </Box>
                )}
                {skuBadges}
            </Box>
        </div>
    ) : (
        <div data-at={Sephora.debug.dataAt('product_refinement')}>{skuBadges}</div>
    );
};

const styles = {
    wrapper: {
        position: 'relative'
    },
    wrapperMinHeight: {
        [mediaQueries.md]: {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitBoxPack: 'end',
            minHeight: fontSizes.base * lineHeights.tight * 2
        }
    },
    variationText: {
        marginRight: space[1],
        display: 'inline-block',
        minHeight: '1em'
    },
    hidden: {
        visibility: 'hidden'
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0
    }
};

export default wrapFunctionalComponent(SwatchDescription, 'SwatchDescription');
